import { GIOI_HAN_FILE_SAO_KE_BYTES, type StatementPreview } from '@qlk/shared';
import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';

import {
  DataTable,
  EmptyState,
  Money,
  Nut,
  PageHeader,
  StatusBadge,
  type CotBang,
} from '../../../ui/index.js';
import {
  docTepThanhBase64,
  useDanhSachKenhChon,
  useDoiTac,
  useTemplate,
  useXacNhanSaoKe,
  useXemTruocSaoKe,
} from '../api.js';
import { OChonTruong } from '../../../ui/index.js';
import { cacKyGanDay, kyHienTai, tenKy } from '../../../lib/ky.js';

type DongXemTruoc = StatementPreview['rows'][number];

const NHAN_KHOP: Record<DongXemTruoc['matchedBy'], { text: string; tone: 'ok' | 'info' | 'warn' }> =
  {
    youtube_id: { text: 'Khớp theo mã YouTube', tone: 'ok' },
    handle: { text: 'Khớp theo handle', tone: 'ok' },
    name: { text: 'Khớp theo tên', tone: 'info' },
    none: { text: 'Chưa khớp — chọn tay', tone: 'warn' },
  };

const BUOC = ['Chọn file', 'Khớp kênh', 'Xác nhận'];

export function TrangImportSaoKe() {
  const [thamSo] = useSearchParams();
  const dieuHuong = useNavigate();

  const doiTac = useDoiTac();
  const xemTruoc = useXemTruocSaoKe();
  const xacNhan = useXacNhanSaoKe();

  const [buoc, datBuoc] = useState(0);
  const [partnerId, datPartnerId] = useState(thamSo.get('partnerId') ?? '');
  const [period, datPeriod] = useState(kyHienTai());
  const [templateId, datTemplateId] = useState('');
  const [file, datFile] = useState<File | null>(null);
  const [loiTep, datLoiTep] = useState<string | null>(null);

  const template = useTemplate(partnerId || undefined);
  const danhSachKenh = useDanhSachKenhChon();

  /** Kênh người dùng chọn tay cho các dòng chưa khớp, theo số thứ tự dòng. */
  const [ganTay, datGanTay] = useState<Record<number, string>>({});

  const dong = xemTruoc.data?.rows ?? [];

  async function doc(): Promise<void> {
    datLoiTep(null);
    if (!file || !partnerId) return;
    if (file.size > GIOI_HAN_FILE_SAO_KE_BYTES) {
      datLoiTep('File sao kê vượt quá 10 MB, hãy chia nhỏ hoặc nén lại file');
      return;
    }

    const contentBase64 = await docTepThanhBase64(file);
    await xemTruoc.mutateAsync(
      {
        partnerId,
        period,
        currency: 'USD',
        fileName: file.name,
        contentBase64,
        templateId: templateId || null,
      },
      {
        onSuccess: () => {
          datGanTay({});
          datBuoc(1);
        },
      },
    );
  }

  function kenhCuaDong(d: DongXemTruoc, i: number): string | null {
    return d.matchedChannelId ?? (ganTay[i] || null);
  }

  const chuaKhop = dong.filter((d, i) => !kenhCuaDong(d, i)).length;

  const cot: CotBang<DongXemTruoc & { stt: number }>[] = [
    {
      key: 'nguon',
      header: 'Dòng trên file',
      render: (d) => <span className="font-medium text-chu">{d.rawChannelRef}</span>,
    },
    {
      key: 'khop',
      header: 'Khớp kênh',
      render: (d) =>
        d.matchedChannelId ? (
          <StatusBadge tone={NHAN_KHOP[d.matchedBy].tone}>
            {NHAN_KHOP[d.matchedBy].text}
          </StatusBadge>
        ) : (
          <select
            aria-label={`Chọn kênh cho dòng ${d.rawChannelRef}`}
            value={ganTay[d.stt] ?? ''}
            onChange={(e) => {
              datGanTay({ ...ganTay, [d.stt]: e.target.value });
            }}
            className="vung-bam rounded-[10px] border border-vien bg-be-mat px-3 text-co-ban text-chu"
          >
            <option value="">Chưa khớp — chọn tay</option>
            {(danhSachKenh.data?.items ?? []).map((k) => (
              <option key={k.id} value={k.id}>
                {k.name}
              </option>
            ))}
          </select>
        ),
    },
    { key: 'gop', header: 'Gộp', align: 'right', render: (d) => <Money value={d.gross} /> },
    {
      key: 'phanNw',
      header: 'Phần NW',
      align: 'right',
      render: (d) => <Money value={d.partnerShare} />,
    },
    { key: 'net', header: 'Thực nhận', align: 'right', render: (d) => <Money value={d.net} /> },
  ];

  return (
    <>
      <PageHeader
        breadcrumb={['VẬN HÀNH', 'Network (MCN)']}
        title="Tải sao kê Network"
        description="Ba bước: đọc file theo template, khớp kênh rồi xác nhận ghi vào hệ thống."
        actions={
          <Link to="/network" className="text-co-ban font-semibold text-chu-phu hover:text-chu">
            ← Danh sách Network
          </Link>
        }
      />

      {/* Thanh ba bước */}
      <ol className="mb-6 flex flex-wrap gap-3">
        {BUOC.map((b, i) => (
          <li
            key={b}
            className={`flex items-center gap-2 rounded-full px-4 py-2 text-co-ban font-medium ${
              i === buoc
                ? 'bg-nhan text-white'
                : i < buoc
                  ? 'bg-ok-nen text-ok-chu'
                  : 'bg-neutral-nen text-chu-phu'
            }`}
          >
            <span className="so-lieu">{i + 1}</span>
            {b}
          </li>
        ))}
      </ol>

      {buoc === 0 && (
        <form
          className="max-w-2xl rounded-the border border-vien bg-be-mat p-6"
          onSubmit={(e) => {
            e.preventDefault();
            void doc();
          }}
        >
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <OChonTruong
              label="Đối tác"
              value={partnerId}
              onChange={(v) => {
                datPartnerId(v);
                datTemplateId('');
              }}
              options={[
                { value: '', label: 'Chọn đối tác…' },
                ...(doiTac.data ?? []).map((d) => ({ value: d.id, label: d.name })),
              ]}
            />
            <OChonTruong
              label="Kỳ sao kê"
              value={period}
              onChange={datPeriod}
              options={cacKyGanDay(12).map((p) => ({ value: p, label: tenKy(p) }))}
            />
            <OChonTruong
              label="Template import"
              value={templateId}
              onChange={datTemplateId}
              options={[
                { value: '', label: 'Tự đoán cột theo tiêu đề file' },
                ...(template.data ?? []).map((t) => ({ value: t.id, label: t.name })),
              ]}
              className="sm:col-span-2"
              moTa="Chưa có template thì hệ thống đoán cột theo tên tiêu đề thường gặp."
            />

            <div className="sm:col-span-2">
              <label htmlFor="tep-sao-ke" className="mb-1 block text-phu font-medium text-chu-phu">
                File sao kê (.csv hoặc .xlsx, tối đa 10 MB)
              </label>
              <input
                id="tep-sao-ke"
                type="file"
                accept=".csv,.tsv,.xlsx"
                required
                className="vung-bam w-full rounded-[10px] border border-vien bg-be-mat px-3 py-2 text-co-ban"
                onChange={(e) => {
                  datFile(e.target.files?.[0] ?? null);
                  datLoiTep(null);
                }}
              />
            </div>
          </div>

          {(loiTep ?? xemTruoc.error) && (
            <p className="mt-4 rounded-[10px] bg-bad-nen px-4 py-3 text-co-ban text-bad-chu">
              {loiTep ?? xemTruoc.error?.message}
            </p>
          )}

          <div className="mt-6">
            <Nut
              kieu="chinh"
              type="submit"
              dangChay={xemTruoc.isPending}
              disabled={!partnerId || !file}
            >
              Xem trước
            </Nut>
          </div>
        </form>
      )}

      {buoc === 1 && (
        <section className="rounded-the border border-vien bg-be-mat p-6">
          <div className="mb-4 flex flex-wrap items-baseline justify-between gap-2">
            <h2 className="text-tieu-de-hop font-bold text-chu">
              {dong.length} dòng · {tenKy(period)}
            </h2>
            <p className={`text-co-ban ${chuaKhop > 0 ? 'text-warn-chu' : 'text-ok-chu'}`}>
              {chuaKhop > 0
                ? `${String(chuaKhop)} dòng chưa khớp kênh — chọn tay hoặc để trống`
                : 'Mọi dòng đã khớp kênh'}
            </p>
          </div>

          <DataTable
            caption="Xem trước các dòng của sao kê"
            columns={cot}
            rows={dong.map((d, i) => ({ ...d, stt: i }))}
            keyOf={(d) => `${String(d.stt)}-${d.rawChannelRef}`}
            tongKet={{
              nguon: 'Tổng',
              gop: <Money value={xemTruoc.data?.totals.gross} />,
              phanNw: <Money value={xemTruoc.data?.totals.partnerShare} />,
              net: <Money value={xemTruoc.data?.totals.net} />,
            }}
            khiRong={<EmptyState title="File không có dòng nào" />}
          />

          <div className="mt-6 flex flex-wrap gap-3">
            <Nut
              onClick={() => {
                datBuoc(0);
              }}
            >
              ← Chọn lại file
            </Nut>
            <Nut
              kieu="chinh"
              onClick={() => {
                datBuoc(2);
              }}
            >
              Tiếp tục
            </Nut>
          </div>
        </section>
      )}

      {buoc === 2 && xemTruoc.data && (
        <section className="max-w-2xl rounded-the border border-vien bg-be-mat p-6">
          <h2 className="text-tieu-de-hop font-bold text-chu">Xác nhận ghi sao kê</h2>
          <dl className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <dt className="text-phu text-chu-mo">Đối tác</dt>
              <dd className="font-semibold text-chu">
                {(doiTac.data ?? []).find((d) => d.id === partnerId)?.name ?? partnerId}
              </dd>
            </div>
            <div>
              <dt className="text-phu text-chu-mo">Kỳ</dt>
              <dd className="font-semibold text-chu">{tenKy(period)}</dd>
            </div>
            <div>
              <dt className="text-phu text-chu-mo">Số dòng</dt>
              <dd className="so-lieu font-semibold text-chu">
                {dong.length} ({chuaKhop} chưa khớp)
              </dd>
            </div>
            <div>
              <dt className="text-phu text-chu-mo">Thực nhận</dt>
              <dd className="font-semibold text-ok-chu">
                <Money value={xemTruoc.data.totals.net} />
              </dd>
            </div>
          </dl>

          <p className="mt-4 text-co-ban text-chu-phu">
            Sao kê cũ của cùng đối tác và kỳ sẽ bị thay bằng dữ liệu này, trạng thái đặt về “Chờ đối
            soát”.
          </p>

          {xacNhan.error && (
            <p className="mt-4 rounded-[10px] bg-bad-nen px-4 py-3 text-co-ban text-bad-chu">
              {xacNhan.error.message}
            </p>
          )}

          <div className="mt-6 flex flex-wrap gap-3">
            <Nut
              onClick={() => {
                datBuoc(1);
              }}
            >
              ← Xem lại
            </Nut>
            <Nut
              kieu="chinh"
              dangChay={xacNhan.isPending}
              onClick={() => {
                xacNhan.mutate(
                  {
                    partnerId,
                    period,
                    currency: 'USD',
                    sourceFile: file?.name ?? null,
                    lines: dong.map((d, i) => ({
                      rawChannelRef: d.rawChannelRef,
                      channelId: kenhCuaDong(d, i),
                      gross: Number(d.gross),
                      partnerShare: Number(d.partnerShare),
                      net: Number(d.net),
                    })),
                  },
                  {
                    onSuccess: () => {
                      void dieuHuong('/network');
                    },
                  },
                );
              }}
            >
              Xác nhận và ghi
            </Nut>
          </div>
        </section>
      )}
    </>
  );
}
